<template>
    <div class="parkinglot-grid-container" :class="overflowClass">
        <parking-lot-sub-campaign-selector
            class="parking-lot-header"
            parking-lot-page
            @change="handleSelectorChange"
        />

        <overview-filter page-section="parkingLot" @change="updateFilter" />

        <div class="quick-filter-bar">
            <!-- Note: the select already has a label so that's why not specifying a for attr on this label -->
            <label class="quick-filter-bar__label"> {{ $t('general.labels.quickFilter') }}: </label>
            <v-text-field
                v-model="quickFilterText"
                class="rtls-text-field rtls-text-field--white pb-2"
                autocomplete="off"
                :placeholder="toSentenceCase($t('placeholders.quickFilter'))"
                @input="updateQuickFilter"
            />
        </div>

        <promo-ag-grid
            ref="parkingLotGrid"
            :row-data="rowData"
            :column-defs="columnDefs"
            :default-col-def="defaultColDef"
            :grid-options="gridOptions"
            grid-style="width: 100%; height: 100%;"
            grid-class="ag-theme-custom__parkinglot-grid"
            dom-layout="normal"
            @grid-ready="onGridReady"
        />

        <div class="status-bar__action--left">
            <create-promotion-overview
                @create-promotion="openDialog()"
                @set-promotion-type="setPromotionType"
                @set-promotion-name="setPromotionName"
            />
            <download-resource-button
                class="parkinglot-grid-container__download-btn"
                :is-secondary-btn="true"
                :resource="resourcesEnum.promotions"
                :format="excel"
                :text-btn="true"
                :label="$t('parkingLot.buttons.export')"
                :download-action="downloadAction"
                :where="where"
                :export-schema="exportSchema"
            />
        </div>

        <v-btn
            class="status-bar__action--right"
            primary
            :disabled="allocationDialogDisabled"
            @click="allocatePromos"
        >
            {{ toSentenceCase($t('parkingLot.allocateSelectedPromotions')) }}
        </v-btn>

        <promotion-viewer-dialog
            ref="promotion-viewer-dialog"
            :key="`promotion-viewer::${selectedPromotion._id}::${promotionToggle}`"
            :promotion="selectedPromotion"
            :split-promotion="splitPromotion"
            :promotion-type="selectedPromotionType"
            :promotion-name="promotionName"
            @close="closePromoViewerDialog"
        />
        <multi-allocation-dialog
            ref="multi-allocation-dialog"
            :start-date="selectedDateRange[0]"
            :end-date="selectedDateRange[1]"
            :promotion-ids="selectedPromotionIds"
            :has-activator="false"
            :success-callback="allocatePromotions"
        />
    </div>
</template>

<script>
import { markRaw } from 'vue';
import { mapMutations, mapState } from 'vuex';
import { debounce, get, isEmpty } from 'lodash';
import parkingLotStates from '@enums/parking-lot-states';
import exportFormats from '@enums/export-formats';
import resourcesEnum from '@enums/resources';
import promotionUtils from '@sharedModules/promotion-utils';
import parkingLotGridMixin from '../mixins/parking-lot-grids-mixin.vue';
import agGridUtils from '@/js/utils/ag-grid-utils';
import { toSentenceCase } from '@/js/utils/string-utils';

const { excel } = exportFormats;
const { getPromotionStatus } = promotionUtils;

export default {
    mixins: [parkingLotGridMixin],
    data() {
        return {
            // The actual value of promotionToggle is not important,
            // changing it on dialog close is just used to force the component
            // to be recreated by referencing the toggle in the key for the dialog.
            promotionToggle: true,
            quickFilterText: '',
            gridOptions: markRaw({
                onSelectionChanged: this.onSelectionChanged,
                rowClassRules: {
                    'grid__disabled-row': params => !params.data.isInParkingLot,
                },
                rowHeight: 35, // Specified in pixels.
                headerHeight: 50,
                rowSelection: {
                    mode: 'multiRow',
                    headerCheckbox: false,
                    selectAll: 'filtered',
                    enableClickSelection: false,
                    checkboxes: false,
                    // checkboxLocation: ''
                    isRowSelectable: params => {
                        const status = getPromotionStatus({
                            moment: this.$moment,
                            startDate: params.data.startDate,
                            isInParkingLot: params.data.isInParkingLot,
                        });
                        return status !== parkingLotStates.planned;
                    },
                },
                // only use checkboxes, can't select row by clicking
                // suppressRowClickSelection: true,
                suppressContextMenu: true,
                columnTypes: {
                    numericColumnCustom: agGridUtils.columnTypes.numericColumnCustom,
                    filterEnabled: {
                        filter: true,
                        menuTabs: ['filterMenuTab'],
                    },
                },
                statusBar: {
                    statusPanels: [
                        { statusPanel: 'agSelectedRowCountComponent', align: 'right' },
                        { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'center' },
                    ],
                },
                getRowId: ({ data }) => data._id,
                overlayLoadingTemplate: `<span class="ag-overlay-loading-center">${toSentenceCase(
                    this.$t('actions.loading')
                )}</span>`,
                overlayNoRowsTemplate: `<span class="ag-overlay-loading-center">${toSentenceCase(
                    this.$tkey('grid.noRows')
                )}</span>`,
            }),
            where: {},
            filterWhere: {},
            excel,
            resourcesEnum,
            downloadAction: 'downloadParkingLot',
            exportSchema: 'parking-lot-flat',
        };
    },
    computed: {
        ...mapState(['parkingLotFilters']),
        ...mapState('clientConfig', ['overviewPageConfig', 'toggleLogic']),

        allocationDialogDisabled() {
            return isEmpty(this.selectedPromotionIds);
        },
        selectedDateRange() {
            if (!this.parkingLotFilters.selections.dateRange) {
                return ['', ''];
            }
            return this.parkingLotFilters.selections.dateRange;
        },
    },
    mounted() {
        this.refetchPromotions();
    },
    created() {
        this.buildWhereClause();
        this.identifierColumns = this.getIdentifierColumns();
        this.detailColumns = this.getDetailsColumns({
            columnGroups: this.overviewPageConfig.parkingLot.detailColumns,
        });

        this.columnDefs = [
            ...this.identifierColumns,
            ...this.getActionColumns({ isPastPromotions: false }),
            ...this.detailColumns,
            {
                children: [
                    {
                        minWidth: 55,
                        width: 55,
                        sortable: false,
                        flex: 1,
                        headerComponent: 'selectAllHeader',
                        headerClass: 'last-header',
                        cellRenderer: 'parkingLotStatusRenderer',
                        valueGetter: params => {
                            return getPromotionStatus({
                                moment: this.$moment,
                                startDate: params.data.startDate,
                                isInParkingLot: params.data.isInParkingLot,
                            });
                        },
                        filterParams: {
                            values: Object.keys(parkingLotStates),
                            valueFormatter: params =>
                                toSentenceCase(this.$tkey(`statusColumn.${params.value}`)),
                            suppressSorting: true,
                        },
                        filter: 'agSetColumnFilter',
                        menuTabs: ['filterMenuTab'],
                    },
                ],
            },
        ];

        // fetching all parking lot promotions
        this.refetchPromotions();
    },
    events: {
        onAllocatePromotions(promotions) {
            const changes = {
                updatedItems: [],
                newItems: [],
            };
            const transformedPromotions = this.transformRowData(promotions);
            transformedPromotions.forEach(promotion => {
                promotion.isInParkingLot = true;
                if (this.promotionsInGrid.has(promotion._id)) {
                    changes.updatedItems.push(promotion);
                } else {
                    changes.newItems.push(promotion);
                }
            });
            this.upsertGridData(changes);

            // we need to update styles and params for the cells
            // flushAsyncTransactions help to get fresh data before refresh
            this.gridApi.flushAsyncTransactions();
            this.gridApi.refreshCells({
                force: true,
            });
        },
    },
    methods: {
        ...mapMutations('promotions', ['setUnsavedPromotion', 'setAddProductsInProgress']),
        buildPromotionFilter() {
            const filter = {};

            if (this.parkingLotFilters.selections.dateRange) {
                filter.startDate = { $gte: this.parkingLotFilters.selections.dateRange[0] };
                filter.endDate = { $lte: this.parkingLotFilters.selections.dateRange[1] };
            }

            // try to get scenario categories, fallback to subCampaign, then campaign if unavailable.
            const categories =
                get(this.parkingLotFilters.selections.scenario, 'categories') ||
                get(this.parkingLotFilters.selections.subCampaign, 'categories') ||
                get(this.parkingLotFilters.selections.campaign, 'categories');
            if (categories) {
                // Users can see promotions which currently have no categories
                filter.$or = [{ categories: { $in: categories } }, { categories: [] }];
            }

            return filter;
        },

        buildWhereClause() {
            this.where = {
                $and: [
                    { $or: [{ isInParkingLot: true }, { wasInParkingLot: true }] },
                    this.buildPromotionFilter(),
                    this.filterWhere,
                ],
            };
        },

        updateFilter({ where }) {
            this.filterWhere = where;
            this.handleSelectorChange();
        },

        /**
         * Filter the parking lot promotions based on user selections in the filter
         */
        handleSelectorChange() {
            this.gridApi.deselectAll();
            this.buildWhereClause();
            this.refetchPromotions();
        },

        allocatePromotions(promotoionIds) {
            if (!this.gridApi) this.refetchPromotions();
            else {
                this.gridApi.deselectAll();

                const rowNodes = [];
                promotoionIds.forEach(_id => {
                    rowNodes.push(this.gridApi.getRowNode(_id));
                });

                const callbackFunction = () => {
                    this.gridApi.redrawRows({ rowNodes });
                };
                this.upsertGridData({
                    updatedItems: rowNodes.map(node => node.data),
                    callbackFunction,
                });
            }
        },
        closePromoViewerDialog() {
            this.setUnsavedPromotion({
                namespace: this.selectedPromotion._id,
                tab: 'all',
                value: false,
            });
            this.setAddProductsInProgress(false);
            this.promotionToggle = !this.promotionToggle;
        },
        allocatePromos() {
            this.$refs['multi-allocation-dialog'].openDialog();
        },

        updateQuickFilter: debounce(function() {
            this.$refs.parkingLotGrid.gridApi.setGridOption(
                'quickFilterText',
                this.quickFilterText
            );
        }, 300),
    },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';
@import '@style/base/_mixins.scss';

.quick-filter-bar {
    @include promo-component-border($top: true);

    width: 100%;
    padding: 1rem 0 0 1rem;
    display: flex;
    background-color: $promo-table-white-bg-colour;

    &__label {
        color: $filter-bar-text-colour;
        line-height: 2.8rem;
        font-size: 1.4rem;
        font-weight: 300;
        margin-right: 1rem;
    }

    .rtls-text-field {
        max-width: 40rem;
    }
}

.parking-lot-header {
    width: 100%;
    background-color: $promo-white;
    padding: 2rem 0;
}

.parkinglot-grid-container {
    height: 100%;
    width: 100%;
    display: flex;
    flex-grow: 1;
    flex-direction: column;
}

:deep(.promo-tag) {
    border-radius: 12px;
    padding: 4px 8px;
    align-items: center;
    background-color: lightgrey;
}

:deep(.last-header .ag-header-cell-text) {
    padding-left: 1rem !important;
}

:deep(.ag-column-drop-wrapper) {
    margin-bottom: 1rem;
}

.allocation-row {
    background-color: $promo-white;
    margin-left: 0px !important;
    width: 100%;
}

.overflow-win {
    :deep(.ag-center-cols-viewport) {
        overflow-x: hidden;
    }
}

.overflow-default {
    :deep(.ag-center-cols-viewport) {
        overflow-x: auto;
    }
}

:deep(.center-cell-items) {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    justify-content: center;
}

:deep(.ag-status-bar) {
    min-height: 5rem;
}

:deep(.ag-status-bar-right) {
    margin-right: 20rem;
}

:deep(.status-bar__action--right) {
    position: absolute;
    bottom: 1.5rem;
    border-top: none;
    right: 2rem;
}

:deep(.status-bar__action--left) {
    height: 2.5rem;
    position: absolute;
    bottom: 1rem;
    border-top: none;
    left: 2rem;
    display: flex;
    gap: 4rem;

    .v-input__details {
        display: none;
    }
}

:deep(.promotion-rag-colour) {
    margin-top: 0 !important;
}

:deep(.ag-status-panel-total-and-filtered-row-count) {
    padding-top: 12px;
}
</style>
